import { ShowMoreFooter } from "@/components/ShowMoreFooter";
import type { HistoryEntryFilter } from "@/features/history/utils";
import { STAGE_ACTIONS_SECTION_IDS } from "@/features/project/projectTopSection/stages/config";
import { SectionItem } from "@/features/tasks/SectionItem";
import { type Opportunity } from "@repo/api";
import { ListView } from "@repo/skapa";
import { T, t } from "@repo/translation";
import { FilterCheckbox, FilterPill } from "@repo/ui";
import { hasItems, isChecked, trackAnalytics } from "@repo/utils";
import { useState } from "react";

import { FilterListContainer } from "../projects/filter/FilterListContainer";
import { HistoryItem } from "./historyItems/HistoryItem";
import { useHistoryEntries } from "./useHistoryEntries";

interface TimelineProps {
  opportunity: Opportunity;
}

export const History = ({ opportunity }: TimelineProps) => {
  const [filters, setFilters] = useState<HistoryEntryFilter[]>([]);
  const [isShowAll, setIsShowAll] = useState(false);

  const { visibleActivities, allEntries } = useHistoryEntries({
    opportunity,
    isShowAll,
    filter: filters,
  });

  const handleCheckFilter = (value: HistoryEntryFilter) =>
    filters.includes(value)
      ? setFilters((current) => current.filter((f) => f !== value))
      : setFilters((current) => [...current, value]);

  return (
    <SectionItem
      id={STAGE_ACTIONS_SECTION_IDS.HISTORY_SECTION}
      title={t("label.previous")}
      action={
        <FilterPill
          id="filter"
          label={
            filters.length
              ? `${t("label.filter")} (${filters.length})`
              : `${t("label.filter")}`
          }
          dropDownItems={
            <FilterListContainer id="history-filter">
              <FilterCheckbox
                id="APPOINTMENT"
                value="APPOINTMENT"
                label={t("label.appointments")}
                checked={isChecked(filters, "APPOINTMENT")}
                onCheckFilter={handleCheckFilter}
              />
              <FilterCheckbox
                id="ORDER"
                value="ORDER"
                label={t("label.orders")}
                checked={isChecked(filters, "ORDER")}
                onCheckFilter={handleCheckFilter}
              />
              <FilterCheckbox
                id="PLANNER"
                value="PLANNER"
                label={t("label.planner_drawings")}
                checked={isChecked(filters, "PLANNER")}
                onCheckFilter={handleCheckFilter}
              />
              <FilterCheckbox
                id="TASK_UPDATE"
                value="TASK_UPDATE"
                label={t("label.tasks")}
                checked={isChecked(filters, "TASK_UPDATE")}
                onCheckFilter={handleCheckFilter}
              />
              <FilterCheckbox
                id="FILES"
                value="FILES"
                label={t("label.files")}
                checked={isChecked(filters, "FILES")}
                onCheckFilter={handleCheckFilter}
              />
            </FilterListContainer>
          }
          rightAlign
        />
      }
    >
      {hasItems(allEntries) ? (
        <>
          {hasItems(visibleActivities) ? (
            <ListView id="project-details-list" size="small">
              {visibleActivities?.map((a) => (
                <HistoryItem
                  key={a.id}
                  history={a}
                  opportunity={opportunity}
                  allHistories={visibleActivities}
                />
              ))}
            </ListView>
          ) : (
            <T translationKey="prompt.no_history_for_filter" />
          )}
          {allEntries.length > visibleActivities.length && (
            <ShowMoreFooter
              visibleCount={visibleActivities.length}
              totalCount={allEntries.length}
              isShowAll={isShowAll}
              onToggle={() => {
                setIsShowAll(!isShowAll);
                trackAnalytics("view_all_history");
              }}
              showToggle={!hasItems(filters)}
            />
          )}
        </>
      ) : (
        <T translationKey="prompt.no_history" />
      )}
    </SectionItem>
  );
};
